import { Controller } from "@hotwired/stimulus"
import { spinnerCycler } from "../spinner_cycler"
import { recordChatUrl } from "../chat_url"

export default class extends Controller {
  static targets = ["messagesWrapper", "messages", "form", "input", "submitButton", "pendingTemplate"]
  // style "v2": the new chat page (providers/chat_v2/_chat), whose optimistic turn matches its markup
  static values = { url: String, style: String }

  connect() {
    // The address names this chat (chat_url.js).
    recordChatUrl(this.urlValue)
    this.scrollChatToBottom()
    this.updateTimestamps()
    this.startTimestampInterval()
    this.focusInput()

    this.stalenessTimeout = null
    this.lastSubmittedMessage = null

    // Ensure input is enabled on connect (in case it was disabled from previous state)
    this.checkAndEnableInput()

    // Initialize spinner cycling for existing spinners
    this.initializeSpinnerCycling()

    // Listen for turbo stream render events to ensure we scroll when server updates arrive
    this.boundHandleTurboRender = this.handleTurboRender.bind(this)
    document.addEventListener('turbo:render', this.boundHandleTurboRender)
    
    // Also listen for Turbo Stream specific events
    this.boundHandleTurboStreamRender = this.handleTurboStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundHandleTurboStreamRender)
    document.addEventListener('turbo:after-stream-render', this.boundHandleTurboStreamRender)
    
    // Use MutationObserver to watch for new spinner elements being added
    this.setupSpinnerObserver()
    
    // Also check for resize to handle image loading or other layout changes
    this.resizeObserver = new ResizeObserver(() => {
      this.scrollChatToBottom()
    })
    
    if (this.hasMessagesTarget) {
      this.resizeObserver.observe(this.messagesTarget)
    }
  }

  disconnect() {
    this.clearStalenessTimeout()
    if (this.timestampInterval) {
      clearInterval(this.timestampInterval)
    }
    if (this.spinnerInitTimeout) {
      clearTimeout(this.spinnerInitTimeout)
    }
    document.removeEventListener('turbo:render', this.boundHandleTurboRender)
    document.removeEventListener('turbo:before-stream-render', this.boundHandleTurboStreamRender)
    document.removeEventListener('turbo:after-stream-render', this.boundHandleTurboStreamRender)
    if (this.spinnerObserver) {
      this.spinnerObserver.disconnect()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    // Stop all spinner cycling when controller disconnects
    spinnerCycler.stopAll()
  }

  handleTurboRender() {
    this.handleDOMUpdate()
  }

  handleTurboStreamRender() {
    // Use a small delay to ensure DOM is fully updated
    setTimeout(() => {
      this.handleDOMUpdate()
    }, 50)
  }

  handleDOMUpdate() {
    // Slight delay to ensure DOM has updated
    requestAnimationFrame(() => {
      this.scrollChatToBottom()
      this.updateTimestamps()
      
      // Clean up disconnected spinners and initialize spinner cycling for any new spinners
      spinnerCycler.cleanupDisconnectedElements()
      this.initializeSpinnerCycling()
      
      // Check if we should re-enable input (when no spinners are present)
      this.checkAndEnableInput()

      this.resetStalenessTimeout()
    })
  }

  setupSpinnerObserver() {
    // Observe the wrapper so we catch when chat-messages div is replaced entirely
    const containerToObserve = this.hasMessagesWrapperTarget 
      ? this.messagesWrapperTarget 
      : (this.hasMessagesTarget ? this.messagesTarget : null)
    
    if (!containerToObserve) {
      return
    }

    // Debounce timer for spinner initialization
    this.spinnerInitTimeout = null

    this.spinnerObserver = new MutationObserver((mutations) => {
      let hasNewSpinners = false
      
      mutations.forEach((mutation) => {
        // Check for added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node itself is a spinner
            if (node.matches && node.matches('[data-spinner-type]')) {
              hasNewSpinners = true
            }
            // Check if the added node contains spinners (including if it's the chat-messages div itself)
            if (node.querySelectorAll) {
              const spinners = node.querySelectorAll('[data-spinner-type]')
              if (spinners.length > 0) {
                hasNewSpinners = true
              }
            }
          }
        })
      })
      
      if (hasNewSpinners) {
        // Clear any pending timeout
        if (this.spinnerInitTimeout) {
          clearTimeout(this.spinnerInitTimeout)
        }
        // Debounce: wait a bit to ensure all DOM updates are complete and avoid rapid re-initializations
        this.spinnerInitTimeout = setTimeout(() => {
          spinnerCycler.cleanupDisconnectedElements()
          this.initializeSpinnerCycling()
          this.checkAndEnableInput()
          this.spinnerInitTimeout = null
        }, 50)
      } else {
        // If no new spinners were added, check if spinners were removed
        // This handles the case where spinners are removed via Turbo Stream updates
        this.checkAndEnableInput()
      }
    })

    // Observe the container for child additions (this will catch when chat-messages div is replaced)
    this.spinnerObserver.observe(containerToObserve, {
      childList: true,
      subtree: true
    })
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Don't submit if input is disabled
      if (this.hasInputTarget && this.inputTarget.disabled) {
        event.preventDefault()
        return
      }
      event.preventDefault()
      this.formTarget.requestSubmit()
    }
  }


  handleSubmitStart(event) {
    const message = this.inputTarget.value.trim()
    if (!message) {
      return
    }

    // Disable input and submit button while awaiting response
    this.disableInput()
    this.setProcessingLock()

    // Create and append user message immediately
    const userMessageHtml = this.createUserMessageHtml(message)
    this.messagesTarget.insertAdjacentHTML('beforeend', userMessageHtml)

    // Clean up any disconnected spinners before creating a new one
    spinnerCycler.cleanupDisconnectedElements()

    // Create and append thinking spinner immediately
    const spinnerHtml = this.createSpinnerHtml()
    this.messagesTarget.insertAdjacentHTML('beforeend', spinnerHtml)

    // Initialize cycling for the newly created spinner
    requestAnimationFrame(() => {
      // Find all thinking spinners and get the one that's NOT already cycling
      const spinnerElements = this.messagesTarget.querySelectorAll('[data-spinner-type="thinking"]')
      
      // Find the newest spinner (one that doesn't have data-spinner-cycling set yet AND is not already in active cyclers)
      let spinnerElement = null
      for (let i = spinnerElements.length - 1; i >= 0; i--) {
        const element = spinnerElements[i]
        const isAlreadyCycling = element.dataset.spinnerCycling === 'true'
        const isInActiveCyclers = spinnerCycler.isCycling(element)
        const isConnected = element.isConnected
        
        // Only use this element if it's connected, not already cycling, and not in active cyclers
        if (isConnected && !isAlreadyCycling && !isInActiveCyclers) {
          spinnerElement = element
          break
        }
      }
      
      if (spinnerElement) {
        spinnerCycler.startCycling(spinnerElement, 'thinking')
      }
    })

    // Clear input and scroll
    setTimeout(() => {
      this.inputTarget.value = ''
    }, 10)
    
    // Force scroll after a tiny delay to ensure layout is complete
    requestAnimationFrame(() => {
      this.scrollChatToBottom()
    })

    this.lastSubmittedMessage = message
    this.startStalenessTimeout()
  }

  createUserMessageHtml(message) {
    if (this.styleValue === "v2") return this.createV2TurnHtml(message)

    // Matches app/views/providers/_chat_message.html.erb user message structure
    const timestamp = new Date().toISOString()
    return `
      <div class="flex flex-col items-end gap-0 w-full">
        <div class="bg-support-dark-200 flex flex-col gap-[10px] items-end justify-center px-4 py-1 rounded-lg max-w-[75%]">
          <p class="text-sm leading-[1.5] text-support-dark-800 w-full">
            ${this.escapeHtml(message)}
          </p>
        </div>
        <p class="text-xs font-medium leading-[1.5] text-support-dark-400" data-timestamp="${timestamp}">
          Just now
        </p>
      </div>
    `
  }

  // The new chat page's turn while it waits for its first update: the member's message and
  // Goodbill at work, cloned from the server-rendered pending template (providers/chat_v2/_pending_template).
  createV2TurnHtml(message) {
    const pending = this.hasPendingTemplateTarget ? this.pendingTemplateTarget.innerHTML : ""
    return `
      <article class="search-chat__turn" aria-busy="true" data-optimistic-turn>
        <div class="search-chat__user"><span class="sr-only">You: </span>${this.escapeHtml(message)}</div>
        ${pending}
      </article>
    `
  }

  createSpinnerHtml() {
    // The new chat page's turn already carries its own "working" line.
    if (this.styleValue === "v2") return ""

    // Matches app/views/providers/_processing.html.erb structure
    // Generate unique ID for this spinner
    const spinnerId = `spinner-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    return `
      <div class="flex flex-col items-start gap-1 w-full">
        <p class="text-xs font-medium leading-[1.5] text-support-dark-600 mb-0">
          Assistant
        </p>
        <div class="flex gap-[3px] items-center pl-0 pr-4 py-4 rounded" data-spinner-type="thinking" data-spinner-id="${spinnerId}">
          <div class="relative shrink-0 size-6">
            <svg class="animate-spin h-6 w-6 text-core-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p class="text-sm leading-[1.5] text-support-dark-400">
            Thinking...
          </p>
        </div>
      </div>
    `
  }

  initializeSpinnerCycling() {
    // Initialize cycling for all spinners in the messages container
    if (this.hasMessagesTarget) {
      spinnerCycler.initializeSpinners(this.messagesTarget)
    } else {
      spinnerCycler.initializeSpinners(this.element)
    }
  }

  scrollChatToBottom() {
    if (this.hasMessagesWrapperTarget) {
      this.messagesWrapperTarget.scrollTop = this.messagesWrapperTarget.scrollHeight
    }
  }

  startTimestampInterval() {
    this.timestampInterval = setInterval(this.updateTimestamps.bind(this), 30000)
  }

  updateTimestamps() {
    const timestampElements = this.element.querySelectorAll('[data-timestamp]')
    timestampElements.forEach((element) => {
      const timestampStr = element.getAttribute('data-timestamp')
      if (timestampStr) {
        const timestamp = new Date(timestampStr)
        const timeAgo = this.timeAgoInWords(timestamp)
        element.textContent = timeAgo
      }
    })
  }

  timeAgoInWords(date) {
    const now = new Date()
    const diffMs = now - date
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffSecs / 60)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)
    const diffWeeks = Math.floor(diffDays / 7)
    const diffMonths = Math.floor(diffDays / 30)
    const diffYears = Math.floor(diffDays / 365)
    
    if (diffSecs < 10) return 'Just now'
    if (diffSecs < 60) return `${diffSecs} second${diffSecs !== 1 ? 's' : ''} ago`
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
    if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
    return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  focusInput() {
    if (this.hasInputTarget) {
      // Use a small timeout to ensure the element is fully interactive
      setTimeout(() => {
        this.inputTarget.focus()
      }, 50)
    }
  }

  setProcessingLock() {
    const lock = document.getElementById('chat-processing-lock')
    if (lock) lock.innerHTML = '<span data-locked="true"></span>'
  }

  clearProcessingLock() {
    const lock = document.getElementById('chat-processing-lock')
    if (lock) lock.innerHTML = ''
  }

  disableInput() {
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  enableInput() {
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
      // Re-focus the input after enabling
      setTimeout(() => {
        this.inputTarget.focus()
      }, 50)
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      // Remove the pulse animation added by forms.js on submit
      this.submitButtonTarget.classList.remove('animate-pulse')
    }
  }

  checkAndEnableInput() {
    // The new chat page: a turn re-rendered as still at work (its "taking longer" note gone with
    // the old markup) is watched again, lock or not.
    if (this.styleValue === "v2" && this.hasMessagesTarget && !this.stalenessTimeout &&
        this.messagesTarget.querySelector('[data-chat-pending]:not([data-slow])')) {
      this.startStalenessTimeout()
    }

    // Don't re-enable while the server is still processing (lock is cleared
    // by broadcast_processing_complete when the background job finishes).
    const processingLock = document.getElementById('chat-processing-lock')
    if (processingLock && processingLock.innerHTML.trim() !== '') {
      return
    }

    // Check if there are any spinners in the messages container
    // If no spinners are present, re-enable the input
    if (this.hasMessagesTarget) {
      // Today's spinners, or the new chat page's "Goodbill at work" line.
      const spinners = this.messagesTarget.querySelectorAll('[data-spinner-type], [data-chat-pending]')
      const hasActiveSpinners = Array.from(spinners).some(spinner => {
        // Check if spinner is still connected to DOM and visible
        return spinner.isConnected && spinner.offsetParent !== null
      })
      
      if (!hasActiveSpinners) {
        this.clearStalenessTimeout()
        this.enableInput()
      } else if (this.styleValue === "v2" && this.hasFormTarget) {
        // A page that arrives with a turn already being worked on (the first search, a reload):
        // lock the composer and watch for a stuck turn, as a submit here would. (The read-only
        // view has no composer and nothing to retry: it just watches.)
        this.disableInput()
        if (!this.stalenessTimeout) this.startStalenessTimeout()
      }
    }
  }

  startStalenessTimeout() {
    this.clearStalenessTimeout()
    this.stalenessTimeout = setTimeout(() => {
      this.handleStaleness()
    }, 60000)
  }

  resetStalenessTimeout() {
    if (this.stalenessTimeout) {
      this.startStalenessTimeout()
    }
  }

  clearStalenessTimeout() {
    if (this.stalenessTimeout) {
      clearTimeout(this.stalenessTimeout)
      this.stalenessTimeout = null
    }
  }

  handleStaleness() {
    this.stalenessTimeout = null

    if (!this.hasMessagesTarget) return
    if (this.styleValue === "v2") return this.handleV2Staleness()

    const spinners = this.messagesTarget.querySelectorAll('[data-spinner-type]')
    spinners.forEach(spinner => {
      const wrapper = spinner.closest('.flex.flex-col.items-start')
      if (wrapper) {
        wrapper.remove()
      } else {
        spinner.remove()
      }
    })
    spinnerCycler.cleanupDisconnectedElements()

    const errorHtml = this.createErrorMessageHtml()
    this.messagesTarget.insertAdjacentHTML('beforeend', errorHtml)

    this.clearProcessingLock()
    this.enableInput()
    this.scrollChatToBottom()
  }

  // The new chat page: a turn with no update for a minute says so in its own "working" line, with
  // Refresh (the page as it now stands: the answer if it came, else the turn as the server has it).
  // Not a resend: that would leave the first question behind, unanswered.
  handleV2Staleness() {
    // The composer stays locked: the turn may still be answered, and a second one would interleave.
    const pending = Array.from(this.messagesTarget.querySelectorAll('[data-chat-pending]')).pop()
    if (!pending) {
      // Nothing is being worked on (the lock's clearing update was lost): let the member go on.
      this.clearProcessingLock()
      this.enableInput()
      return
    }
    if (pending.dataset.slow) return // said so already; don't pull a member who scrolled up back down

    pending.dataset.slow = "true"
    pending.querySelector('.typing-dots')?.remove()
    const status = pending.querySelector('.search-chat__status')
    if (status) status.textContent = "This is taking longer than usual."
    pending.insertAdjacentHTML('beforeend', '<button type="button" class="search-chat__retry" data-action="provider-chat#retryLastMessage">Refresh</button>')
    this.scrollChatToBottom()
  }

  // The new chat page: a follow-up the server turned down (the error it sent says why) never
  // gets an answer, so its turn goes and the member can go on.
  handleSubmitEnd(event) {
    if (this.styleValue !== "v2" || event.detail.success) return

    this.messagesTarget.querySelectorAll('[data-optimistic-turn]').forEach(turn => turn.remove())
    this.clearProcessingLock()
    this.enableInput()
  }

  createErrorMessageHtml() {
    return `
      <div class="flex flex-col items-start gap-1 w-full">
        <p class="text-xs font-medium leading-[1.5] text-support-dark-600 mb-0">
          Assistant
        </p>
        <div class="bg-support-dark-550 flex flex-col gap-[10px] items-end justify-center px-4 py-1 rounded-lg max-w-[75%]">
          <p class="text-sm leading-[1.5] text-support-light-200 w-full">
            Something went wrong and I wasn't able to respond.
          </p>
        </div>
        <button type="button" data-action="provider-chat#retryLastMessage" class="text-sm font-medium text-core-purple-500 hover:text-core-purple-700 mt-1">
          Try again
        </button>
      </div>
    `
  }

  retryLastMessage() {
    // The new chat page: reload it (see handleV2Staleness).
    if (this.styleValue === "v2") {
      window.location.reload()
      return
    }

    if (!this.lastSubmittedMessage || !this.hasInputTarget || !this.hasFormTarget) return

    const errorWrapper = this.messagesTarget.querySelector('[data-action="provider-chat#retryLastMessage"]')?.closest('.flex.flex-col.items-start')
    if (errorWrapper) {
      errorWrapper.remove()
    }

    this.inputTarget.value = this.lastSubmittedMessage
    this.inputTarget.disabled = false
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('animate-pulse')
    }
    this.formTarget.requestSubmit()
  }
}

